import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration axios avec retry et timeout
const axiosInstance = axios.create({
  timeout: 30000, // 30 secondes timeout
});

// Fonction de retry automatique
const retryRequest = async (fn, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (error) {
      const isLastAttempt = i === retries - 1;
      const isNetworkError = !error.response;
      const isServerError = error.response?.status >= 500;
      
      if (isLastAttempt || (!isNetworkError && !isServerError)) {
        throw error;
      }
      
      console.warn(`[API] Tentative ${i + 1}/${retries} échouée, retry dans ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      delay *= 1.5; // Backoff exponentiel
    }
  }
};

// Wrapper pour les appels API avec retry
const apiCall = async (method, url, data = null, config = {}) => {
  return retryRequest(async () => {
    const response = await axiosInstance({
      method,
      url,
      data,
      ...config
    });
    return response.data;
  });
};

export const api = {
  // App Installations
  trackAppInstall: async (data) => {
    return apiCall('post', `${API}/app/install`, data);
  },

  getAppInstalls: async () => {
    return apiCall('get', `${API}/app/installs`);
  },

  getAppInstallsCount: async () => {
    return apiCall('get', `${API}/app/installs/count`);
  },

  // Credit Management
  getCredits: async () => {
    const response = await axios.get(`${API}/credits`);
    return response.data;
  },

  getCreditClient: async (clientId) => {
    const response = await axios.get(`${API}/credits/${clientId}`);
    return response.data;
  },

  createCredit: async (data) => {
    const response = await axios.post(`${API}/credits`, data);
    return response.data;
  },

  createCreditClient: async (data) => {
    const response = await axios.post(`${API}/credits`, data);
    return response.data;
  },

  addCreditPayment: async (clientId, data) => {
    const response = await axios.post(`${API}/credits/${clientId}/payment`, data);
    return response.data;
  },

  addCreditTransaction: async (clientId, data) => {
    const response = await axios.post(`${API}/credits/${clientId}/transaction`, data);
    return response.data;
  },

  uploadCreditId: async (clientId, idPhoto) => {
    const response = await axios.post(`${API}/credits/${clientId}/upload-id`, { idPhoto });
    return response.data;
  },

  uploadCreditSignature: async (clientId, signature) => {
    const response = await axios.post(`${API}/credits/${clientId}/signature`, { signature });
    return response.data;
  },

  searchCreditClient: async (query) => {
    const response = await axios.get(`${API}/credits/search`, { params: { query } });
    return response.data;
  },

  checkCustomerDebt: async (customerId) => {
    const response = await axios.get(`${API}/credits/check/${customerId}`);
    return response.data;
  },

  checkCustomerDebtByPhone: async (phone) => {
    const response = await axios.get(`${API}/credits/check-by-phone/${phone}`);
    return response.data;
  },

  findCreditClientByPhone: async (phone) => {
    const response = await axios.get(`${API}/credits/find-by-phone/${phone}`);
    return response.data;
  },

  linkCreditToCustomer: async (clientId, customerId) => {
    const response = await axios.post(`${API}/credits/${clientId}/link-customer`, null, { params: { customer_id: customerId } });
    return response.data;
  },

  // Prospects / Marketing
  createProspect: async (data) => {
    const response = await axios.post(`${API}/prospects`, data);
    return response.data;
  },

  getProspects: async () => {
    const response = await axios.get(`${API}/prospects`);
    return response.data;
  },

  confirmProspectEmail: async (token) => {
    const response = await axios.get(`${API}/prospects/confirm/${token}`);
    return response.data;
  },

  sendProspectEmail: async (prospectId, emailType) => {
    const response = await axios.post(`${API}/prospects/${prospectId}/send-email`, { emailType });
    return response.data;
  },

  sendBulkProspectEmails: async (prospectIds, emailType) => {
    const response = await axios.post(`${API}/prospects/bulk-email`, { prospectIds, emailType });
    return response.data;
  },

  markProspectViewed: async (prospectId) => {
    const response = await axios.patch(`${API}/prospects/${prospectId}/view`);
    return response.data;
  },

  markAllProspectsViewed: async () => {
    const response = await axios.post(`${API}/prospects/mark-all-viewed`);
    return response.data;
  },

  searchProspect: async (params) => {
    const response = await axios.get(`${API}/prospects/search`, { params });
    return response.data;
  },

  useProspectPromo: async (prospectId, vendorCode) => {
    const response = await axios.post(`${API}/prospects/${prospectId}/use-promo`, null, { params: { vendor_code: vendorCode } });
    return response.data;
  },

  convertProspectToCustomer: async (prospectId) => {
    const response = await axios.post(`${API}/prospects/${prospectId}/convert-to-customer`);
    return response.data;
  },

  unsubscribeProspect: async (prospectId) => {
    const response = await axios.post(`${API}/prospects/${prospectId}/unsubscribe`);
    return response.data;
  },

  // Relances automatiques
  sendRelanceEmails: async (relanceType = 'auto') => {
    const response = await axios.post(`${API}/prospects/send-relance`, null, { params: { relance_type: relanceType } });
    return response.data;
  },

  getRelanceStats: async () => {
    const response = await axios.get(`${API}/prospects/relance-stats`);
    return response.data;
  },

  // CRON : Lancer les relances automatiques
  triggerCronReminders: async () => {
    const response = await axios.post(`${API}/cron/send-reminders`);
    return response.data;
  },

  // Notifications admin - nouveaux prospects
  getNewProspectsCount: async () => {
    const response = await axios.get(`${API}/prospects/notifications/count`);
    return response.data;
  },

  markAllProspectsViewed: async () => {
    const response = await axios.post(`${API}/prospects/notifications/mark-viewed`);
    return response.data;
  },

  // Google Auth
  googleAuthCallback: async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId });
    return response.data;
  },

  getAuthMe: async (token) => {
    const response = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  logout: async (token) => {
    const response = await axios.post(`${API}/auth/logout`, null, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Vendor endpoints
  validateVendorCode: async (code) => {
    const response = await axios.post(`${API}/vendor/validate`, { code });
    return response.data;
  },

  getVendorCodes: async () => {
    const response = await axios.get(`${API}/vendor/codes`);
    return response.data;
  },

  createVendorCode: async (code, name) => {
    const response = await axios.post(`${API}/vendor/codes`, { code, name });
    return response.data;
  },

  deleteVendorCode: async (codeId) => {
    const response = await axios.delete(`${API}/vendor/codes/${codeId}`);
    return response.data;
  },

  toggleVendorCode: async (codeId) => {
    const response = await axios.patch(`${API}/vendor/codes/${codeId}/toggle`);
    return response.data;
  },

  updateVendorCode: async (codeId, data) => {
    const response = await axios.patch(`${API}/vendor/codes/${codeId}`, data);
    return response.data;
  },

  getVendorLeaderboard: async () => {
    const response = await axios.get(`${API}/vendor/leaderboard`);
    return response.data;
  },

  setEpicierDuMois: async (codeId) => {
    const response = await axios.post(`${API}/vendor/codes/${codeId}/epicier-du-mois`);
    return response.data;
  },

  // Admin endpoints
  adminLogin: async (password) => {
    const response = await axios.post(`${API}/admin/login`, { password });
    return response.data;
  },

  // Supreme Admin (Vaisseau Mère Golem 13)
  supremeLogin: async (code) => {
    const response = await axios.post(`${API}/auth/supreme/login`, { code });
    return response.data;
  },

  getAdminConnections: async (limit = 50) => {
    const response = await axios.get(`${API}/auth/admin/connections?limit=${limit}`);
    return response.data;
  },

  verifyAdmin2FA: async (sessionId, code) => {
    const response = await axios.post(`${API}/admin/verify-2fa`, { session_id: sessionId, code });
    return response.data;
  },

  resendAdmin2FA: async (sessionId) => {
    const response = await axios.post(`${API}/admin/resend-2fa`, { session_id: sessionId });
    return response.data;
  },

  getSettings: async () => {
    const response = await axios.get(`${API}/admin/settings`);
    return response.data;
  },

  updateSettings: async (pinEnabled) => {
    const response = await axios.patch(`${API}/admin/settings`, { pinEnabled });
    return response.data;
  },

  getStats: async () => {
    const response = await axios.get(`${API}/admin/stats`);
    return response.data;
  },

  searchAllCustomers: async (search = '') => {
    const response = await axios.get(`${API}/admin/customers?search=${search}`);
    return response.data;
  },

  // Modifier le numéro de téléphone d'un client (Admin)
  updateCustomerPhone: async (customerId, newPhone) => {
    const response = await axios.patch(`${API}/admin/customers/${customerId}/phone`, {
      new_phone: newPhone
    });
    return response.data;
  },

  // Promotions endpoints
  getActivePromotions: async () => {
    const response = await axios.get(`${API}/promotions/active`);
    return response.data;
  },

  getAllPromotions: async () => {
    const response = await axios.get(`${API}/promotions`);
    return response.data;
  },

  createPromotion: async (data) => {
    const response = await axios.post(`${API}/promotions`, data);
    return response.data;
  },

  togglePromotion: async (promoId) => {
    const response = await axios.patch(`${API}/promotions/${promoId}/toggle`);
    return response.data;
  },

  deletePromotion: async (promoId) => {
    const response = await axios.delete(`${API}/promotions/${promoId}`);
    return response.data;
  },

  // Recommendations endpoints
  getRecommendations: async () => {
    const response = await axios.get(`${API}/recommendations`);
    return response.data;
  },

  // Export endpoints
  exportDailySales: async (date = null) => {
    const url = date ? `${API}/export/daily-sales?date=${date}` : `${API}/export/daily-sales`;
    const response = await axios.get(url);
    return response.data;
  },

  exportCustomers: async () => {
    const response = await axios.get(`${API}/export/customers`);
    return response.data;
  },

  // Customer endpoints
  createCustomer: async (name, phone, pin, referrerCode = null, birthDate = null, guardianFirstName = null, guardianLastName = null, guardianPhone = null) => {
    const response = await axios.post(`${API}/customers`, { 
      name, 
      phone, 
      pin,
      referrerCode,
      birthDate,
      guardianFirstName,
      guardianLastName,
      guardianPhone
    });
    return response.data;
  },

  searchCustomer: async (query) => {
    // Recherche par téléphone ou nom
    const response = await axios.get(`${API}/customers/search?query=${encodeURIComponent(query)}`);
    return response.data;
  },

  getCustomerById: async (customerId) => {
    // Récupérer un client par son ID
    const response = await axios.get(`${API}/customers/${customerId}`);
    return response.data;
  },

  searchCustomersList: async (query) => {
    // Recherche retournant une liste
    const response = await axios.get(`${API}/customers/search-all?query=${encodeURIComponent(query)}`);
    return response.data;
  },

  // Connexion sécurisée client (téléphone + PIN 4 chiffres)
  clientLogin: async (phone, pin) => {
    const response = await axios.post(`${API}/customers/login`, { phone, pin });
    return response.data;
  },

  // Email Auth (Magic Code)
  sendEmailAuthCode: async (email) => {
    const response = await axios.post(`${API}/auth/email/send-code`, { email });
    return response.data;
  },

  verifyEmailAuthCode: async (email, code) => {
    const response = await axios.post(`${API}/auth/email/verify-code`, { email, code });
    return response.data;
  },

  // ========== 2FA TOTP ==========
  
  // Initialiser le 2FA - génère QR code
  setupTOTP: async (customerId) => {
    const response = await axios.post(`${API}/totp/setup/${customerId}`);
    return response.data;
  },

  // Vérifier le code TOTP et activer le 2FA
  verifyTOTP: async (customerId, token) => {
    const response = await axios.post(`${API}/totp/verify`, { customerId, token });
    return response.data;
  },

  // Valider le code TOTP lors de la connexion
  validateTOTP: async (customerId, token) => {
    const response = await axios.post(`${API}/totp/validate`, { customerId, token });
    return response.data;
  },

  // Désactiver le 2FA (admin)
  disableTOTP: async (customerId) => {
    const response = await axios.delete(`${API}/totp/${customerId}`);
    return response.data;
  },

  // Obtenir le statut 2FA
  getTOTPStatus: async (customerId) => {
    const response = await axios.get(`${API}/totp/${customerId}/status`);
    return response.data;
  },

  // ========== 2FA ADMIN ==========
  
  // Obtenir le statut 2FA Admin
  getAdmin2FAStatus: async () => {
    const response = await axios.get(`${API}/admin/2fa/status`);
    return response.data;
  },

  // Configurer le 2FA Admin (génère QR code)
  setupAdmin2FA: async (adminCode) => {
    const response = await axios.post(`${API}/admin/2fa/setup`, { adminCode });
    return response.data;
  },

  // Vérifier et activer le 2FA Admin
  verifyAdmin2FASetup: async (adminCode, totpCode) => {
    const response = await axios.post(`${API}/admin/2fa/verify`, { adminCode, totpCode });
    return response.data;
  },

  // Valider le code 2FA lors de la connexion admin
  validateAdmin2FALogin: async (adminCode, totpCode) => {
    const response = await axios.post(`${API}/admin/2fa/validate`, { adminCode, totpCode });
    return response.data;
  },

  // Désactiver le 2FA Admin
  disableAdmin2FA: async (adminCode, totpCode) => {
    const response = await axios.delete(`${API}/admin/2fa/disable`, { data: { adminCode, totpCode } });
    return response.data;
  },

  // Mettre à jour le profil client
  updateCustomerProfile: async (customerId, profileData) => {
    const response = await axios.put(`${API}/customers/${customerId}/profile`, profileData);
    return response.data;
  },

  // Signer l'autorisation parentale (pour mineurs)
  signParentalConsent: async (customerId, signature, signerName, authorizedCards) => {
    const response = await axios.post(`${API}/customers/${customerId}/parental-consent`, {
      signature,
      signerName,
      authorizedCards
    });
    return response.data;
  },

  getCustomerByQR: async (qrCode) => {
    const response = await axios.get(`${API}/customers/qr/${qrCode}`);
    return response.data;
  },

  getCustomer: async (customerId) => {
    const response = await axios.get(`${API}/customers/${customerId}`);
    return response.data;
  },

  // Supprimer un client (nécessite code admin)
  deleteCustomer: async (customerId, adminCode) => {
    const response = await axios.delete(`${API}/customers/${customerId}?adminCode=${adminCode}`);
    return response.data;
  },

  // Réinitialiser le PIN d'un client
  resetCustomerPin: async (customerId, newPin, vendorCode = null) => {
    const response = await axios.post(`${API}/customers/${customerId}/reset-pin`, {
      newPin,
      vendorCode
    });
    return response.data;
  },

  // Changer le code d'un vendeur
  changeVendorCode: async (vendorId, currentCode, newCode) => {
    const response = await axios.post(`${API}/vendor/codes/${vendorId}/change-code`, {
      currentCode,
      newCode
    });
    return response.data;
  },

  // Régénérer le code parrain (plus mémorisable)
  regenerateReferralCode: async (customerId) => {
    const response = await axios.post(`${API}/customers/${customerId}/regenerate-referral`);
    return response.data;
  },

  customizeReferralCode: async (customerId, newCode) => {
    const response = await axios.post(`${API}/customers/${customerId}/customize-referral`, { new_code: newCode });
    return response.data;
  },

  // Messagerie
  sendMessage: async (toCustomerId, content) => {
    const response = await axios.post(`${API}/messages/send`, { toCustomerId, content });
    return response.data;
  },

  // Envoyer un message ciblé (par type de carte ou clients spécifiques)
  sendTargetedMessage: async (content, target, recipientIds = null) => {
    const response = await axios.post(`${API}/messages/send-targeted`, { content, target, recipientIds });
    return response.data;
  },

  // Client envoie un message à l'admin
  clientSendMessage: async (customerId, content) => {
    const response = await axios.post(`${API}/messages/customer/${customerId}/send`, { content });
    return response.data;
  },

  getCustomerMessages: async (customerId) => {
    const response = await axios.get(`${API}/messages/customer/${customerId}`);
    return response.data;
  },

  // Récupérer toutes les conversations (admin)
  getConversations: async () => {
    const response = await axios.get(`${API}/messages/conversations`);
    return response.data;
  },

  markMessageRead: async (messageId) => {
    const response = await axios.post(`${API}/messages/${messageId}/read`);
    return response.data;
  },

  getAllMessages: async () => {
    const response = await axios.get(`${API}/messages/all`);
    return response.data;
  },

  // Relance SMS clients livrés
  sendRelanceSMS: async (phoneNumbers, message) => {
    const response = await axios.post(`${API}/sms/relance`, { phoneNumbers, message });
    return response.data;
  },

  updateNotifications: async (customerId, notificationsEnabled, pushSubscription = null) => {
    const response = await axios.patch(`${API}/customers/${customerId}/notifications`, {
      notificationsEnabled,
      pushSubscription
    });
    return response.data;
  },

  // Push Notifications
  savePushToken: async (customerId, fcmToken) => {
    // Si fcmToken est null, on désactive les notifications
    if (fcmToken === null) {
      const response = await axios.post(`${API}/push/unregister`, {
        customerId
      });
      return response.data;
    }
    
    const response = await axios.post(`${API}/push/register`, {
      customerId,
      fcmToken,
      platform: 'web'
    });
    return response.data;
  },

  // Envoyer une notification push à tous les clients ou à un client spécifique
  sendPushNotification: async (title, body, type = 'promo', customerId = null, data = {}) => {
    const response = await axios.post(`${API}/push/send`, {
      title,
      body,
      type,
      customerId,
      data
    });
    return response.data;
  },

  // Récupérer le nombre de tokens push actifs
  getPushTokensCount: async () => {
    const response = await axios.get(`${API}/push/tokens`);
    return response.data;
  },

  // ============== JEUX / GAMES ==============
  
  // Vérifier si le client peut jouer aujourd'hui
  canPlayGame: async (customerId, gameType = 'wheel') => {
    const response = await axios.get(`${API}/games/status/${customerId}`);
    return response.data;
  },

  // Enregistrer une partie jouée
  recordGamePlay: async (customerId, gameType, prizeType, prizeValue = null, score = null, isTraining = false) => {
    const response = await axios.post(`${API}/games/play`, {
      customerId,
      gameType,
      prizeType,
      prizeValue: prizeValue?.toString(),
      score,
      isTraining
    });
    return response.data;
  },

  // Statut des jeux du client (parties restantes, points)
  getGameStatus: async (customerId) => {
    const response = await axios.get(`${API}/games/status/${customerId}`);
    return response.data;
  },

  // Historique des parties d'un client
  getGameHistory: async (customerId, limit = 20) => {
    const response = await axios.get(`${API}/games/history/${customerId}?limit=${limit}`);
    return response.data;
  },

  // Stats des jeux (admin)
  getGameStats: async () => {
    const response = await axios.get(`${API}/games/stats`);
    return response.data;
  },

  applyReferralCode: async (customerId, referralCode) => {
    const response = await axios.post(`${API}/customers/${customerId}/referral?referralCode=${referralCode}`);
    return response.data;
  },

  checkBadges: async (customerId) => {
    const response = await axios.post(`${API}/customers/${customerId}/badges/check`);
    return response.data;
  },

  getCustomerCards: async (customerId) => {
    const response = await axios.get(`${API}/cards/customer/${customerId}`);
    return response.data;
  },

  // Card endpoints
  stampCard: async (cardId, boxIndex, value, vendorCode = null) => {
    const response = await axios.post(`${API}/cards/${cardId}/stamp`, { boxIndex, value, vendorCode });
    return response.data;
  },

  redeemCard: async (cardId, lineIndex = null) => {
    const response = await axios.post(`${API}/cards/${cardId}/redeem`, { lineIndex });
    return response.data;
  },

  getCardsHistory: async (customerId = null) => {
    const url = customerId ? `${API}/cards/history?customerId=${customerId}` : `${API}/cards/history`;
    const response = await axios.get(url);
    return response.data;
  },

  // SMS endpoints
  getSMSStatus: async () => {
    const response = await axios.get(`${API}/sms/status`);
    return response.data;
  },

  sendSMS: async (phone, message) => {
    const response = await axios.post(`${API}/sms/send`, { phone, message });
    return response.data;
  },

  notifyNearCompletion: async () => {
    const response = await axios.post(`${API}/sms/notify-near-completion`);
    return response.data;
  },

  getSMSLogs: async (limit = 50) => {
    const response = await axios.get(`${API}/sms/logs?limit=${limit}`);
    return response.data;
  },

  // SMS en masse (multi-filtres)
  previewSMS: async (cardFilters) => {
    const filters = Array.isArray(cardFilters) ? cardFilters : [cardFilters];
    const response = await axios.post(`${API}/sms/preview`, { cardFilters: filters, message: '' });
    return response.data;
  },

  sendBulkSMS: async (message, cardFilters) => {
    const filters = Array.isArray(cardFilters) ? cardFilters : [cardFilters];
    const response = await axios.post(`${API}/sms/bulk`, { message, cardFilters: filters });
    return response.data;
  },

  // Classement des vendeurs
  getVendorRanking: async (period = 'month') => {
    const response = await axios.get(`${API}/admin/vendor-ranking?period=${period}`);
    return response.data;
  },

  // Livraison
  createDeliveryRequest: async (data) => {
    const response = await axios.post(`${API}/delivery/request`, data);
    return response.data;
  },

  getDeliveryRequests: async (status = null) => {
    const url = status ? `${API}/delivery/requests?status=${status}` : `${API}/delivery/requests`;
    const response = await axios.get(url);
    return response.data;
  },

  getUnreadDeliveryCount: async () => {
    const response = await axios.get(`${API}/delivery/requests/unread-count`);
    return response.data;
  },

  updateDeliveryRequest: async (deliveryId, data) => {
    const response = await axios.patch(`${API}/delivery/requests/${deliveryId}`, data);
    return response.data;
  },

  markDeliveryRead: async (deliveryId) => {
    const response = await axios.post(`${API}/delivery/requests/${deliveryId}/read`);
    return response.data;
  },

  deleteDeliveryRequest: async (deliveryId) => {
    const response = await axios.delete(`${API}/delivery/requests/${deliveryId}`);
    return response.data;
  },

  // Offline sync
  syncOfflineData: async (stamps, newCustomers) => {
    const response = await axios.post(`${API}/sync/offline`, { stamps, newCustomers });
    return response.data;
  },

  // Chat LPE
  getChatMessages: async (customerId, channel = 'team') => {
    const response = await axios.get(`${API}/chat/messages`, { params: { customerId, channel } });
    return response.data;
  },

  sendChatMessage: async (data) => {
    const response = await axios.post(`${API}/chat/messages`, data);
    return response.data;
  },

  getAdminChatMessages: async (channel) => {
    const response = await axios.get(`${API}/chat/admin/messages`, { params: { channel } });
    return response.data;
  },

  sendAdminChatMessage: async (data) => {
    const response = await axios.post(`${API}/chat/admin/messages`, data);
    return response.data;
  },

  markChatRead: async (customerId) => {
    const response = await axios.post(`${API}/chat/read`, { customerId });
    return response.data;
  },

  markAdminChatRead: async (customerId) => {
    const response = await axios.post(`${API}/chat/admin/read/${customerId}`);
    return response.data;
  },

  getChatStats: async () => {
    const response = await axios.get(`${API}/chat/stats`);
    return response.data;
  },

  // Customer Leaderboard
  getCustomerLeaderboard: async () => {
    const response = await axios.get(`${API}/customers/leaderboard`);
    return response.data;
  },

  // Flash Offers
  getFlashOffers: async () => {
    const response = await axios.get(`${API}/flash-offers`);
    return response.data;
  },

  createFlashOffer: async (data) => {
    const response = await axios.post(`${API}/flash-offers`, data);
    return response.data;
  },

  deleteFlashOffer: async (offerId) => {
    const response = await axios.delete(`${API}/flash-offers/${offerId}`);
    return response.data;
  },

  // Polls / Sondages
  getActivePolls: async () => {
    const response = await axios.get(`${API}/polls`);
    return response.data;
  },

  createPoll: async (data) => {
    const response = await axios.post(`${API}/polls`, data);
    return response.data;
  },

  submitVote: async (pollId, optionIndex, customerId) => {
    const response = await axios.post(`${API}/polls/${pollId}/vote`, { optionIndex, customerId });
    return response.data;
  },

  getCustomerVotes: async (customerId) => {
    const response = await axios.get(`${API}/polls/votes/${customerId}`);
    return response.data;
  },

  deletePoll: async (pollId) => {
    const response = await axios.delete(`${API}/polls/${pollId}`);
    return response.data;
  },

  // News / Actualités
  getNews: async () => {
    const response = await axios.get(`${API}/news`);
    return response.data;
  },

  createNews: async (data) => {
    const response = await axios.post(`${API}/news`, data);
    return response.data;
  },

  likeNews: async (newsId, customerId) => {
    const response = await axios.post(`${API}/news/${newsId}/like`, { customerId });
    return response.data;
  },

  deleteNews: async (newsId) => {
    const response = await axios.delete(`${API}/news/${newsId}`);
    return response.data;
  },

  // Events / Calendrier
  getEvents: async () => {
    const response = await axios.get(`${API}/events`);
    return response.data;
  },

  createEvent: async (data) => {
    const response = await axios.post(`${API}/events`, data);
    return response.data;
  },

  deleteEvent: async (eventId) => {
    const response = await axios.delete(`${API}/events/${eventId}`);
    return response.data;
  },

  // Birthday Gifts
  claimBirthdayGift: async (customerId, gift) => {
    const response = await axios.post(`${API}/customers/${customerId}/birthday-gift`, gift);
    return response.data;
  },

  getBirthdayGifts: async () => {
    const response = await axios.get(`${API}/birthday-gifts`);
    return response.data;
  },

  redeemBirthdayGift: async (giftId) => {
    const response = await axios.post(`${API}/birthday-gifts/${giftId}/redeem`);
    return response.data;
  },

  // ============================================================================
  // MODULE LIVRAISONS
  // ============================================================================

  // Zones de livraison
  getDeliveryZones: async () => {
    const response = await axios.get(`${API}/delivery/zones`);
    return response.data;
  },

  createDeliveryZone: async (zone) => {
    const response = await axios.post(`${API}/delivery/zones`, zone);
    return response.data;
  },

  updateDeliveryZone: async (zoneId, zone) => {
    const response = await axios.put(`${API}/delivery/zones/${zoneId}`, zone);
    return response.data;
  },

  deleteDeliveryZone: async (zoneId) => {
    const response = await axios.delete(`${API}/delivery/zones/${zoneId}`);
    return response.data;
  },

  // Livreurs
  getDrivers: async () => {
    const response = await axios.get(`${API}/delivery/drivers`);
    return response.data;
  },

  getDriversLeaderboard: async () => {
    const response = await axios.get(`${API}/delivery/drivers/leaderboard`);
    return response.data;
  },

  createDriver: async (driver) => {
    const response = await axios.post(`${API}/delivery/drivers`, driver);
    return response.data;
  },

  updateDriverStatus: async (driverId, status) => {
    const response = await axios.put(`${API}/delivery/drivers/${driverId}/status`, { status });
    return response.data;
  },

  deleteDriver: async (driverId) => {
    const response = await axios.delete(`${API}/delivery/drivers/${driverId}`);
    return response.data;
  },

  // Connexion livreur
  driverLogin: async (code) => {
    const response = await axios.post(`${API}/delivery/driver/login?code=${code}`);
    return response.data;
  },

  getDriverOrders: async (driverId) => {
    const response = await axios.get(`${API}/delivery/driver/${driverId}/orders`);
    return response.data;
  },

  getDriverHistory: async (driverId) => {
    const response = await axios.get(`${API}/delivery/driver/${driverId}/history`);
    return response.data;
  },

  // Commandes de livraison
  getDeliveryOrders: async (status, date) => {
    let url = `${API}/delivery/orders`;
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (date) params.append('date', date);
    if (params.toString()) url += `?${params.toString()}`;
    const response = await axios.get(url);
    return response.data;
  },

  getTodayOrders: async () => {
    const response = await axios.get(`${API}/delivery/orders/today`);
    return response.data;
  },

  createDeliveryOrder: async (order) => {
    const response = await axios.post(`${API}/delivery/orders`, order);
    return response.data;
  },

  updateOrderStatus: async (orderId, status, paymentMethod = null) => {
    let url = `${API}/delivery/orders/${orderId}/status?status=${status}`;
    if (paymentMethod) {
      url += `&payment_method=${paymentMethod}`;
    }
    const response = await axios.put(url);
    return response.data;
  },

  // Alias pour compatibilité
  updateDeliveryOrderStatus: async (orderId, status, paymentMethod = null) => {
    let url = `${API}/delivery/orders/${orderId}/status?status=${status}`;
    if (paymentMethod) {
      url += `&payment_method=${paymentMethod}`;
    }
    const response = await axios.put(url);
    return response.data;
  },

  assignDriverToOrder: async (orderId, driverId) => {
    const response = await axios.put(`${API}/delivery/orders/${orderId}/assign`, { driverId });
    return response.data;
  },

  cancelDeliveryOrder: async (orderId) => {
    const response = await axios.delete(`${API}/delivery/orders/${orderId}`);
    return response.data;
  },

  // Créneaux
  getDeliverySlots: async (date) => {
    let url = `${API}/delivery/slots`;
    if (date) url += `?date=${date}`;
    const response = await axios.get(url);
    return response.data;
  },

  // Stats
  getDeliveryStats: async () => {
    const response = await axios.get(`${API}/delivery/stats`);
    return response.data;
  },

  // ============================================
  // ANALYTICS - Tracking du comportement client
  // ============================================
  
  trackAnalyticsEvent: async (event) => {
    const response = await axios.post(`${API}/analytics/event`, event);
    return response.data;
  },

  trackAnalyticsBatch: async (events) => {
    const response = await axios.post(`${API}/analytics/batch`, events);
    return response.data;
  },

  getAnalyticsDashboard: async () => {
    const response = await axios.get(`${API}/analytics/dashboard`);
    return response.data;
  },

  getRecentLogins: async (limit = 50) => {
    const response = await axios.get(`${API}/analytics/recent-logins?limit=${limit}`);
    return response.data;
  },

  getTopClients: async (days = 30, limit = 20) => {
    const response = await axios.get(`${API}/analytics/top-clients?days=${days}&limit=${limit}`);
    return response.data;
  },

  getInactiveClients: async (days = 14, limit = 50) => {
    const response = await axios.get(`${API}/analytics/inactive-clients?days=${days}&limit=${limit}`);
    return response.data;
  },

  getClientAnalytics: async (customerId, days = 30) => {
    const response = await axios.get(`${API}/analytics/client/${customerId}?days=${days}`);
    return response.data;
  },

  getAnalyticsAlerts: async () => {
    const response = await axios.get(`${API}/analytics/alerts`);
    return response.data;
  },

  // Top Parrains
  getTopReferrers: async (limit = 10) => {
    const response = await axios.get(`${API}/referrals/top?limit=${limit}`);
    return response.data;
  },

  // ============================================
  // 🔥 HEARTBEATS - MODULE RENCONTRES
  // ============================================
  
  // Profil Heartbeats
  getHeartbeatsProfile: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/profile/${customerId}`);
    return response.data;
  },

  updateHeartbeatsProfile: async (customerId, data) => {
    const response = await axios.put(`${API}/heartbeats/profile/${customerId}`, data);
    return response.data;
  },

  // Découvrir des profils
  getHeartbeatsProfiles: async (customerId, hasVideo = false) => {
    const response = await axios.get(`${API}/heartbeats/discover/${customerId}?has_video=${hasVideo}`);
    return response.data;
  },

  // 💕 Couples - Trouver des amis couples
  getCoupleProfiles: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/couples/${customerId}`);
    return response.data;
  },

  sendCoupleFriendRequest: async (customerId, targetCoupleId) => {
    const response = await axios.post(`${API}/heartbeats/couples/friend-request/${customerId}?target_couple_id=${targetCoupleId}`);
    return response.data;
  },

  // Crushes
  sendHeartbeatsCrush: async (customerId, targetId) => {
    const response = await axios.post(`${API}/heartbeats/crush/${customerId}`, {
      targetCustomerId: targetId
    });
    return response.data;
  },

  // 💎 Super Likes Dorés
  sendSuperLike: async (customerId, targetId, message = '') => {
    const response = await axios.post(`${API}/heartbeats/superlike/${customerId}`, {
      targetCustomerId: targetId,
      message
    });
    return response.data;
  },

  getReceivedSuperLikes: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/superlikes/received/${customerId}`);
    return response.data;
  },

  getSentSuperLikes: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/superlikes/sent/${customerId}`);
    return response.data;
  },

  markSuperLikeSeen: async (superLikeId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/superlikes/${superLikeId}/seen?customer_id=${customerId}`);
    return response.data;
  },

  getHeartbeatsCrushes: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/crushes/${customerId}`);
    return response.data;
  },

  getHeartbeatsReceivedCrushes: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/crushes/received/${customerId}`);
    return response.data;
  },

  // Matchs
  getHeartbeatsMatches: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/matches/${customerId}`);
    return response.data;
  },

  // Messages
  sendHeartbeatsMessage: async (customerId, matchId, text, isEphemeral = false, replyToId = null) => {
    const response = await axios.post(`${API}/heartbeats/message/${customerId}`, {
      matchId,
      text,
      isEphemeral,
      replyToId
    });
    return response.data;
  },

  getHeartbeatsMessages: async (customerId, matchId) => {
    const response = await axios.get(`${API}/heartbeats/messages/${customerId}/${matchId}`);
    return response.data;
  },

  // Photos (partage avec consentement, expire 24h)
  sendHeartbeatsPhoto: async (customerId, matchId, photoData, maxViews = 1) => {
    const response = await axios.post(`${API}/heartbeats/photo/send/${customerId}`, {
      matchId,
      photoData,
      maxViews
    });
    return response.data;
  },

  respondToHeartbeatsPhoto: async (customerId, photoId, accept) => {
    const response = await axios.post(`${API}/heartbeats/photo/respond/${customerId}`, {
      photoId,
      accept
    });
    return response.data;
  },

  getHeartbeatsPhoto: async (customerId, photoId) => {
    const response = await axios.get(`${API}/heartbeats/photo/${customerId}/${photoId}`);
    return response.data;
  },

  reportHeartbeatsPhoto: async (customerId, photoId, reason) => {
    const response = await axios.post(`${API}/heartbeats/photo/report/${customerId}`, {
      photoId,
      reason
    });
    return response.data;
  },

  getPendingHeartbeatsPhotos: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/photos/pending/${customerId}`);
    return response.data;
  },

  // ============================================
  // 🗑️ SUPPRESSION DE MESSAGES FLAMME
  // ============================================
  
  deleteHeartbeatsMessage: async (customerId, messageId) => {
    const response = await axios.delete(`${API}/heartbeats/message/${customerId}/${messageId}`);
    return response.data;
  },

  markHeartbeatsMessageRead: async (messageId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/message/${messageId}/read/${customerId}`);
    return response.data;
  },

  // ============================================
  // 👁️ GESTION DES VUES DE PHOTOS
  // ============================================
  
  recordHeartbeatsPhotoView: async (photoId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/photo/${photoId}/view/${customerId}`);
    return response.data;
  },

  // ============================================
  // 💬 RÉACTIONS AUX MESSAGES FLAMME
  // ============================================
  
  addHeartbeatsMessageReaction: async (messageId, customerId, reaction) => {
    const response = await axios.post(`${API}/heartbeats/message/${messageId}/react/${customerId}`, {
      reaction
    });
    return response.data;
  },

  removeHeartbeatsMessageReaction: async (messageId, customerId) => {
    const response = await axios.delete(`${API}/heartbeats/message/${messageId}/react/${customerId}`);
    return response.data;
  },

  // ============================================
  // 🟢 PRÉSENCE EN LIGNE & TYPING
  // ============================================
  
  updateHeartbeatsPresence: async (customerId) => {
    const response = await axios.post(`${API}/heartbeats/presence/${customerId}`);
    return response.data;
  },

  getOnlineUsers: async () => {
    const response = await axios.get(`${API}/heartbeats/online`);
    return response.data;
  },

  setTypingStatus: async (customerId, matchId, typing = true) => {
    const response = await axios.post(`${API}/heartbeats/typing/${customerId}/${matchId}?typing=${typing}`);
    return response.data;
  },

  getTypingStatus: async (matchId, customerId) => {
    const response = await axios.get(`${API}/heartbeats/typing/${matchId}/${customerId}`);
    return response.data;
  },

  // ============================================
  // 👋 SYSTÈME DE COUCOU (WAVE)
  // ============================================
  
  sendWave: async (customerId, targetId, message = null) => {
    const response = await axios.post(`${API}/heartbeats/wave/send/${customerId}/${targetId}`, {
      message
    });
    return response.data;
  },

  getWaves: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/waves/${customerId}`);
    return response.data;
  },

  respondToWave: async (waveId, action, customerId) => {
    const response = await axios.post(`${API}/heartbeats/wave/respond/${waveId}?action=${action}&customer_id=${customerId}`);
    return response.data;
  },

  // ============================================
  // 🎤 MESSAGES VOCAUX
  // ============================================
  
  sendVoiceMessage: async (customerId, matchId, audioData, duration) => {
    const response = await axios.post(`${API}/heartbeats/voice/${customerId}`, {
      matchId,
      audioData,
      duration
    });
    return response.data;
  },

  markVoicePlayed: async (messageId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/voice/${messageId}/played/${customerId}`);
    return response.data;
  },

  getHeartbeatsMessageReactions: async (messageId) => {
    const response = await axios.get(`${API}/heartbeats/message/${messageId}/reactions`);
    return response.data;
  },

  // Stats (admin)
  getHeartbeatsStats: async () => {
    const response = await axios.get(`${API}/heartbeats/stats`);
    return response.data;
  },

  // ============================================
  // 👑 SYSTÈME DE COURONNES
  // ============================================
  
  getCrowns: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/crowns/${customerId}`);
    return response.data;
  },

  getLeaderboard: async (limit = 10) => {
    const response = await axios.get(`${API}/heartbeats/leaderboard?limit=${limit}`);
    return response.data;
  },

  likeProfile: async (customerId, targetId) => {
    const response = await axios.post(`${API}/heartbeats/profile/${customerId}/like?target_id=${targetId}`);
    return response.data;
  },

  getProfileLikes: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/profile/${customerId}/likes`);
    return response.data;
  },

  // ============================================
  // 📍 AU MAGASIN MAINTENANT
  // ============================================
  
  storeCheckin: async (customerId) => {
    const response = await axios.post(`${API}/heartbeats/store/checkin/${customerId}`);
    return response.data;
  },

  storeCheckout: async (customerId) => {
    const response = await axios.post(`${API}/heartbeats/store/checkout/${customerId}`);
    return response.data;
  },

  whoIsAtStore: async () => {
    const response = await axios.get(`${API}/heartbeats/store/who-is-here`);
    return response.data;
  },

  pingAtStore: async (customerId, message) => {
    const response = await axios.post(`${API}/heartbeats/store/ping/${customerId}?message=${encodeURIComponent(message)}`);
    return response.data;
  },

  // ============================================
  // 🎁 CADEAUX VIRTUELS
  // ============================================
  
  getGiftsCatalog: async () => {
    const response = await axios.get(`${API}/heartbeats/gifts/catalog`);
    return response.data;
  },

  sendVirtualGift: async (customerId, targetId, giftType, message = '') => {
    const response = await axios.post(`${API}/heartbeats/gift/send/${customerId}?target_id=${targetId}&gift_type=${giftType}&message=${encodeURIComponent(message)}`);
    return response.data;
  },

  getReceivedGifts: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/gifts/received/${customerId}`);
    return response.data;
  },

  claimGiftReward: async (customerId, threshold) => {
    const response = await axios.post(`${API}/heartbeats/gifts/claim/${customerId}?threshold=${threshold}`);
    return response.data;
  },

  // ============================================
  // 🎙️ SALONS VOCAUX
  // ============================================
  
  getVoiceRooms: async () => {
    const response = await axios.get(`${API}/heartbeats/voice-rooms`);
    return response.data;
  },

  getVoiceRoomDetails: async (roomId) => {
    const response = await axios.get(`${API}/heartbeats/voice-room/${roomId}`);
    return response.data;
  },

  joinVoiceRoom: async (roomId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/voice-room/${roomId}/join/${customerId}`);
    return response.data;
  },

  leaveVoiceRoom: async (roomId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/voice-room/${roomId}/leave/${customerId}`);
    return response.data;
  },

  raiseHandVoiceRoom: async (roomId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/voice-room/${roomId}/raise-hand/${customerId}`);
    return response.data;
  },

  allowSpeaker: async (roomId, customerId, targetId) => {
    const response = await axios.post(`${API}/heartbeats/voice-room/${roomId}/allow-speak/${customerId}?target_id=${targetId}`);
    return response.data;
  },

  // ============================================
  // 📹 APPELS VIDÉO
  // ============================================
  
  initiateVideoCall: async (customerId, matchId, offer = null) => {
    const response = await axios.post(`${API}/heartbeats/video/call/${customerId}`, {
      matchId,
      offer
    });
    return response.data;
  },

  getVideoCallStatus: async (callId) => {
    const response = await axios.get(`${API}/heartbeats/video/call/${callId}/status`);
    return response.data;
  },

  answerVideoCall: async (callId, customerId, answer) => {
    const response = await axios.post(`${API}/heartbeats/video/call/${callId}/answer/${customerId}`, {
      answer
    });
    return response.data;
  },

  rejectVideoCall: async (callId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/video/call/${callId}/reject/${customerId}`);
    return response.data;
  },

  endVideoCall: async (callId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/video/call/${callId}/end/${customerId}`);
    return response.data;
  },

  addIceCandidate: async (callId, customerId, candidate) => {
    const response = await axios.post(`${API}/heartbeats/video/call/${callId}/ice/${customerId}`, {
      candidate
    });
    return response.data;
  },

  checkIncomingCall: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/video/incoming/${customerId}`);
    return response.data;
  },

  // ============================================
  // 📸 PHOTOS DE PROFIL
  // ============================================

  uploadProfilePhoto: async (customerId, photoData) => {
    const response = await axios.post(`${API}/heartbeats/profile/${customerId}/photos`, {
      photo_data: photoData
    });
    return response.data;
  },

  getProfilePhotos: async (customerId, includePending = false) => {
    const response = await axios.get(`${API}/heartbeats/profile/${customerId}/photos?include_pending=${includePending}`);
    return response.data;
  },

  deleteProfilePhoto: async (customerId, photoId) => {
    const response = await axios.delete(`${API}/heartbeats/profile/${customerId}/photos/${photoId}`);
    return response.data;
  },

  setMainPhoto: async (customerId, photoId) => {
    const response = await axios.put(`${API}/heartbeats/profile/${customerId}/photos/${photoId}/main`);
    return response.data;
  },

  reportProfilePhoto: async (photoId, reporterId, reason) => {
    const response = await axios.post(`${API}/heartbeats/profile/photos/${photoId}/report`, {
      reporter_id: reporterId,
      reason
    });
    return response.data;
  },

  // ============================================
  // 📹 VIDÉOS DE PROFIL
  // ============================================

  uploadProfileVideo: async (customerId, videoData, duration, caption) => {
    const response = await axios.post(`${API}/video-profile/upload/${customerId}`, {
      videoData,
      duration,
      caption
    });
    return response.data;
  },

  getProfileVideos: async (customerId) => {
    const response = await axios.get(`${API}/video-profile/${customerId}`);
    return response.data;
  },

  viewVideo: async (videoId, viewerId) => {
    const response = await axios.get(`${API}/video-profile/view/${videoId}?viewer_id=${viewerId || ''}`);
    return response.data;
  },

  likeVideo: async (videoId, customerId) => {
    const response = await axios.post(`${API}/video-profile/${videoId}/like?customer_id=${customerId}`);
    return response.data;
  },

  deleteProfileVideo: async (videoId, customerId) => {
    const response = await axios.delete(`${API}/video-profile/${videoId}?customer_id=${customerId}`);
    return response.data;
  },

  // ============================================
  // 📍 LOCALISATION & CROISEMENTS
  // ============================================

  updateLocation: async (customerId, latitude, longitude) => {
    const response = await axios.post(`${API}/heartbeats/location/${customerId}`, {
      latitude,
      longitude
    });
    return response.data;
  },

  getCrossings: async (customerId, days = 7) => {
    const response = await axios.get(`${API}/heartbeats/crossings/${customerId}?days=${days}`);
    return response.data;
  },

  waveFromCrossing: async (crossingId, customerId) => {
    const response = await axios.post(`${API}/heartbeats/crossing/${crossingId}/wave?customer_id=${customerId}`);
    return response.data;
  },

  // ============================================
  // ⚡ SPEED DATING HEBDOMADAIRE
  // ============================================
  
  getSpeedDatingConfig: async () => {
    const response = await axios.get(`${API}/heartbeats/speed-dating/config`);
    return response.data;
  },

  getSpeedDatingStatus: async () => {
    const response = await axios.get(`${API}/heartbeats/speed-dating/status`);
    return response.data;
  },

  joinSpeedDating: async (customerId) => {
    const response = await axios.post(`${API}/heartbeats/speed-dating/join/${customerId}`);
    return response.data;
  },

  startSpeedDating: async (sessionId) => {
    const response = await axios.post(`${API}/heartbeats/speed-dating/${sessionId}/start`);
    return response.data;
  },

  getCurrentSpeedDatingPartner: async (sessionId, customerId) => {
    const response = await axios.get(`${API}/heartbeats/speed-dating/${sessionId}/current/${customerId}`);
    return response.data;
  },

  voteSpeedDating: async (sessionId, customerId, partnerId, vote) => {
    const response = await axios.post(`${API}/heartbeats/speed-dating/${sessionId}/vote/${customerId}`, {
      partner_id: partnerId,
      vote
    });
    return response.data;
  },

  nextSpeedDatingRound: async (sessionId) => {
    const response = await axios.post(`${API}/heartbeats/speed-dating/${sessionId}/next-round`);
    return response.data;
  },

  getSpeedDatingResults: async (sessionId, customerId) => {
    const response = await axios.get(`${API}/heartbeats/speed-dating/${sessionId}/results/${customerId}`);
    return response.data;
  },

  // ============================================
  // 🔧 ADMIN HEARTBEATS
  // ============================================
  
  getAdminHeartbeatsProfiles: async () => {
    const response = await axios.get(`${API}/admin/heartbeats/profiles`);
    return response.data;
  },

  adminToggleHeartbeatsProfile: async (customerId, visible) => {
    const response = await axios.put(`${API}/admin/heartbeats/profile/${customerId}/toggle?visible=${visible}`);
    return response.data;
  },

  adminDeleteHeartbeatsProfile: async (customerId) => {
    const response = await axios.delete(`${API}/admin/heartbeats/profile/${customerId}`);
    return response.data;
  },

  adminBanFromHeartbeats: async (customerId) => {
    const response = await axios.post(`${API}/admin/heartbeats/ban/${customerId}`);
    return response.data;
  },

  getAdminSpeedDates: async () => {
    const response = await axios.get(`${API}/admin/heartbeats/speed-dates`);
    return response.data;
  },

  adminCancelSpeedDate: async (speedDateId) => {
    const response = await axios.delete(`${API}/admin/heartbeats/speed-date/${speedDateId}`);
    return response.data;
  },

  adminHeartbeatsBroadcast: async (title, message) => {
    const response = await axios.post(`${API}/admin/heartbeats/broadcast`, { title, message });
    return response.data;
  },

  getAdminHeartbeatsReports: async () => {
    const response = await axios.get(`${API}/admin/heartbeats/reports`);
    return response.data;
  },

  // Modération photos profil
  getAdminPendingPhotos: async () => {
    const response = await axios.get(`${API}/admin/heartbeats/photos/pending`);
    return response.data;
  },

  adminApprovePhoto: async (photoId) => {
    const response = await axios.post(`${API}/admin/heartbeats/photos/${photoId}/approve`);
    return response.data;
  },

  adminRejectPhoto: async (photoId, reason = 'Contenu inapproprié') => {
    const response = await axios.post(`${API}/admin/heartbeats/photos/${photoId}/reject`, { reason });
    return response.data;
  },

  adminDeletePhoto: async (photoId) => {
    const response = await axios.delete(`${API}/admin/heartbeats/photos/${photoId}`);
    return response.data;
  },

  // ============================================
  // ✅ VÉRIFICATION DE PROFIL
  // ============================================

  startVerification: async (customerId) => {
    const response = await axios.post(`${API}/heartbeats/verify/start/${customerId}`);
    return response.data;
  },

  submitVerificationSelfie: async (verificationId, selfieData) => {
    const response = await axios.post(`${API}/heartbeats/verify/submit/${verificationId}`, {
      selfie_data: selfieData
    });
    return response.data;
  },

  getVerificationStatus: async (customerId) => {
    const response = await axios.get(`${API}/heartbeats/verify/status/${customerId}`);
    return response.data;
  },

  // Admin vérification
  getAdminPendingVerifications: async () => {
    const response = await axios.get(`${API}/admin/heartbeats/verifications/pending`);
    return response.data;
  },

  adminApproveVerification: async (verificationId) => {
    const response = await axios.post(`${API}/admin/heartbeats/verifications/${verificationId}/approve`);
    return response.data;
  },

  adminRejectVerification: async (verificationId, reason = 'La pose ne correspond pas') => {
    const response = await axios.post(`${API}/admin/heartbeats/verifications/${verificationId}/reject`, { reason });
    return response.data;
  },

  reportHeartbeatsUser: async (reporterId, reportedId, reason) => {
    const response = await axios.post(`${API}/heartbeats/report?reporter_id=${reporterId}&reported_id=${reportedId}&reason=${encodeURIComponent(reason)}`);
    return response.data;
  },

  // ============================================
  // 💎 HBT TOKEN SYSTEM
  // ============================================

  // Wallet
  getHBTWallet: async (customerId) => {
    const response = await axios.get(`${API}/hbt/wallet/${customerId}`);
    return response;
  },

  getHBTTransactions: async (customerId, limit = 50) => {
    const response = await axios.get(`${API}/hbt/transactions/${customerId}?limit=${limit}`);
    return response;
  },

  convertCrownsToHBT: async (customerId, crownsAmount) => {
    const response = await axios.post(`${API}/hbt/convert`, {
      customerId,
      crowns_amount: crownsAmount
    });
    return response;
  },

  // Cadeaux Prestige
  getHBTGiftsCatalog: async () => {
    const response = await axios.get(`${API}/hbt/gifts/catalog`);
    return response;
  },

  sendHBTGift: async (data) => {
    const response = await axios.post(`${API}/hbt/gifts/send`, data);
    return response;
  },

  getHBTReceivedGifts: async (customerId) => {
    const response = await axios.get(`${API}/hbt/gifts/received/${customerId}`);
    return response;
  },

  // Salons Privés
  getHBTRoomPlans: async () => {
    const response = await axios.get(`${API}/hbt/rooms/plans`);
    return response;
  },

  createHBTPrivateRoom: async (data) => {
    const response = await axios.post(`${API}/hbt/rooms/create`, data);
    return response;
  },

  getHBTMyRooms: async (customerId) => {
    const response = await axios.get(`${API}/hbt/rooms/my/${customerId}`);
    return response;
  },

  // Parrainage HBT
  registerHBTReferral: async (newCustomerId, referralCode) => {
    const response = await axios.post(`${API}/hbt/referral/register`, {
      newCustomerId,
      referralCode
    });
    return response;
  },

  getHBTReferralStats: async (customerId) => {
    const response = await axios.get(`${API}/hbt/referral/stats/${customerId}`);
    return response;
  },

  // Stats globales
  getHBTGlobalStats: async () => {
    const response = await axios.get(`${API}/hbt/stats/global`);
    return response;
  },

  // ============================================
  // 🌌 COMPATIBILITÉ D'ÂMES
  // ============================================

  getSoulCompatibility: async (customerId, targetId) => {
    const response = await axios.get(`${API}/heartbeats/compatibility/${customerId}/${targetId}`);
    return response;
  },

  // ============================================
  // 🖤 SHADOW MODE - Les 7 Péchés Capitaux
  // ============================================

  // Profil Shadow
  getShadowProfile: async (customerId) => {
    const response = await axios.get(`${API}/shadow/profile/${customerId}`);
    return response;
  },

  saveShadowProfile: async (customerId, data) => {
    const response = await axios.post(`${API}/shadow/profile/${customerId}`, data);
    return response;
  },

  // Compatibilité des péchés
  getShadowCompatibility: async (customerId, targetId) => {
    const response = await axios.get(`${API}/shadow/compatibility/${customerId}/${targetId}`);
    return response;
  },

  // Découverte Shadow
  discoverShadowProfiles: async (customerId, limit = 10) => {
    const response = await axios.get(`${API}/shadow/discover/${customerId}?limit=${limit}`);
    return response;
  },

  // Salons de l'ombre
  getShadowRooms: async () => {
    const response = await axios.get(`${API}/shadow/rooms`);
    return response;
  },

  joinShadowRoom: async (roomId, customerId) => {
    const response = await axios.post(`${API}/shadow/rooms/${roomId}/join?customer_id=${customerId}`);
    return response;
  },

  getShadowRoomMembers: async (roomId) => {
    const response = await axios.get(`${API}/shadow/rooms/${roomId}/members`);
    return response;
  },

  // Stats Shadow
  getShadowStats: async () => {
    const response = await axios.get(`${API}/shadow/stats/global`);
    return response;
  },

  // Liste des péchés
  getAllSins: async () => {
    const response = await axios.get(`${API}/shadow/sins`);
    return response;
  },

  // ============================================
  // 🌙 NYX 2.0 - ZONES D'OMBRE PROFONDES
  // ============================================
  
  getNyx2Profile: async (customerId) => {
    const response = await axios.get(`${API}/shadow/nyx2/profile/${customerId}`);
    return response.data;
  },

  saveNyx2Profile: async (customerId, data) => {
    const response = await axios.post(`${API}/shadow/nyx2/profile/${customerId}`, data);
    return response.data;
  },

  getNyx2Compatibility: async (customerId, targetId) => {
    const response = await axios.get(`${API}/shadow/nyx2/compatibility/${customerId}/${targetId}`);
    return response.data;
  },

  // ============================================
  // CARD TEMPLATES - Cartes de fidélité personnalisées
  // ============================================
  
  getCardTemplates: async (includeInactive = false) => {
    const response = await axios.get(`${API}/card-templates/?include_inactive=${includeInactive}`);
    return response.data;
  },

  getCardTemplate: async (templateId) => {
    const response = await axios.get(`${API}/card-templates/${templateId}`);
    return response.data;
  },

  createCardTemplate: async (templateData) => {
    const response = await axios.post(`${API}/card-templates/`, templateData);
    return response.data;
  },

  updateCardTemplate: async (templateId, updateData) => {
    const response = await axios.patch(`${API}/card-templates/${templateId}`, updateData);
    return response.data;
  },

  deleteCardTemplate: async (templateId, adminCode) => {
    const response = await axios.delete(`${API}/card-templates/${templateId}?admin_code=${adminCode}`);
    return response.data;
  },

  toggleCardTemplate: async (templateId) => {
    const response = await axios.post(`${API}/card-templates/${templateId}/toggle`);
    return response.data;
  },

  createCustomerCard: async (customerId, templateId) => {
    const response = await axios.post(`${API}/card-templates/customer/${customerId}/create-card?template_id=${templateId}`);
    return response.data;
  }
};
